import React, { FC, HTMLAttributes } from 'react';
import { withStyles } from 'tss-react/mui';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Paper,
  TextField,
  FormControl,
  Select,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { makeStyles } from 'tss-react/mui';
import { IncentiveHighlight, HighlightFieldType } from '../../../types/incentive-types';
import { map } from 'lodash';

type DisplaySettingTableProps = {
  isDisabled: boolean;
  highlights: IncentiveHighlight[];
  onAddRow: () => void;
  onDeleteRow: (index: number) => void;
  onMoveRow: (from: number, to: number) => void;
  onModifyField: (index: number, field: keyof IncentiveHighlight, value: string) => void;
};

const useStyles = makeStyles()((theme) => ({
  inputBox: {
    paddingTop: 6,
    paddingBottom: 8
  },
  headerOperationField: {
    display: 'flex',
    justifyContent: 'space-between'
  }
}));

const CustomTableCell = withStyles(TableCell, (theme) => ({
  head: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    fontSize: '1rem',
    paddingTop: 10,
    paddingBottom: 10
  },
  body: {
    fontWeight: 'bold',
    paddingTop: 10,
    paddingBottom: 10
  }
}));

const DisplaySettingTable: FC<DisplaySettingTableProps & HTMLAttributes<HTMLDivElement>> = ({
  isDisabled,
  highlights,
  onAddRow,
  onMoveRow,
  onDeleteRow,
  onModifyField,
  ...rest
}) => {
  const { classes } = useStyles();

  return (
    <>
      {highlights && highlights.length > 0 &&
        <TableContainer {...rest} component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <CustomTableCell>
                  Name
                  <Tooltip
                    title="Please fill in approximately 9 Chinese Words / 18 english characters to prevent app display overflow"
                  >
                    <IconButton
                      style={{ padding: 5, color: 'white', fontSize: '0.5rem' }}
                      disabled={isDisabled}
                    >
                      <InfoIcon fontSize="small"/>
                    </IconButton>
                  </Tooltip>
                </CustomTableCell>
                <CustomTableCell>Type</CustomTableCell>
                <CustomTableCell align="right">
                  <IconButton
                    disabled={isDisabled}
                    style={{ padding: 5, color: 'white' }}
                    onClick={onAddRow}
                  >
                    <AddIcon />
                  </IconButton>
                </CustomTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {highlights.map((highlight, index) => (
                <TableRow key={`highlight-row-${index}`}>
                  <CustomTableCell>
                    <TextField
                      disabled={isDisabled}
                      fullWidth
                      style={{ margin: 0 }}
                      InputProps={{
                        classes: {
                          input: classes.inputBox
                        }
                      }}
                      margin="dense"
                      variant="outlined"
                      value={highlight.name}
                      onChange={e => onModifyField(index, 'name', e.target.value)}
                    />
                  </CustomTableCell>
                  <CustomTableCell>
                    <FormControl
                      fullWidth
                      style={{ margin: 0 }}
                      margin="dense"
                      variant="outlined"
                    >
                      <Select
                        disabled={isDisabled}
                        native
                        value={highlight.type}
                        inputProps={{
                          classes: {
                            root: classes.inputBox,
                          },
                        }}
                        onChange={e => onModifyField(index, 'type', e.target.value as string || '')}
                      >
                        <option aria-label="None" value="" />
                        {map(HighlightFieldType, (option: string) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </CustomTableCell>
                  <CustomTableCell align="right">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <IconButton
                        style={{ padding: 5 }}
                        onClick={() => highlights[index + 1] && onMoveRow(index, index + 1)}
                        disabled={isDisabled || !!!highlights[index + 1]}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                      <IconButton
                        style={{ padding: 5 }}
                        onClick={() => highlights[index - 1] && onMoveRow(index, index - 1)}
                        disabled={isDisabled || !!!highlights[index - 1]}
                      >
                        <ArrowDropUpIcon />
                      </IconButton>
                      <IconButton
                        style={{ padding: 5 }}
                        onClick={() => onDeleteRow(index)}
                        disabled={isDisabled || index === 0}
                      >
                        <HighlightOffIcon />
                      </IconButton>
                    </div>
                  </CustomTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      }
    </>
  )
};

export default DisplaySettingTable;
